import type { ReactNode } from "react";
import { Box, Button, Drawer, Group, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useGo } from "./ConsoleShell";
import { Cursor, KvBox, StatusDot } from "./primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useQueryStore } from "../stores/queryStore";
import { useUiStore } from "../stores/uiStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import { bytes, compact, percent } from "../lib/vf";
import css from "./drawers.module.css";

/** The eight detail slide-overs, one Drawer switched by `ui.detail.kind`. */
export function DetailDrawers() {
  const detail = useUiStore((s) => s.detail);
  const close = useUiStore((s) => s.closeDetail);

  const width = detail?.kind === "member" ? 440 : detail?.kind === "key" || detail?.kind === "profile" ? 460 : 480;

  return (
    <Drawer
      opened={detail !== null}
      onClose={close}
      position="right"
      size={width}
      withCloseButton={false}
      padding={0}
      overlayProps={{ backgroundOpacity: 0.66, color: "#050503" }}
      transitionProps={{ transition: "slide-left", duration: 250 }}
      classNames={{ content: css.drawer, body: css.drawerBody }}
    >
      {detail?.kind === "namespace" ? <NamespaceDetail id={detail.id} /> : null}
      {detail?.kind === "document" ? <DocumentDetail id={detail.id} /> : null}
      {detail?.kind === "operation" ? <OperationDetail id={detail.id} /> : null}
      {detail?.kind === "job" ? <JobDetail id={detail.id} /> : null}
      {detail?.kind === "repo" ? <RepoDetail id={detail.id} /> : null}
      {detail?.kind === "member" ? <MemberDetail id={detail.id} /> : null}
      {detail?.kind === "key" ? <KeyDetail id={detail.id} /> : null}
      {detail?.kind === "profile" ? <ProfileDetail id={detail.id} /> : null}
    </Drawer>
  );
}

function Head({ label, children }: { label: string; children?: ReactNode }) {
  const close = useUiStore((s) => s.closeDetail);
  return (
    <Group justify="space-between" mb={12} wrap="nowrap">
      <Text fz="12px" c="var(--vf-acc)">
        {label}
      </Text>
      <button type="button" className={css.close} onClick={close} aria-label="close">
        [x]
      </button>
      {children}
    </Group>
  );
}

function Title({ children }: { children: ReactNode }) {
  return (
    <Text fz="15px" c="var(--vf-text)" mb={6} style={{ wordBreak: "break-all" }}>
      {children}
    </Text>
  );
}

function Tiles({ items }: { items: { k: string; v: string }[] }) {
  return (
    <Box className={css.tiles} mb={20}>
      {items.map((t) => (
        <Box key={t.k} className={css.tile}>
          <div className={css.tileKey}>{t.k}</div>
          <div className={css.tileValue}>{t.v}</div>
        </Box>
      ))}
    </Box>
  );
}

// ------------------------------------------------------------- namespace ----

function NamespaceDetail({ id }: { id: string }) {
  const go = useGo();
  const close = useUiStore((s) => s.closeDetail);
  const openModal = useUiStore((s) => s.openModal);
  const ns = useConsoleStore((s) => s.namespaces.find((n) => n.id === id));
  const compaction = useConsoleStore((s) => s.compaction);
  const compactNamespace = useConsoleStore((s) => s.compactNamespace);
  const setQueryNamespace = useQueryStore((s) => s.setNamespace);

  if (!ns) return null;
  const compacting = compaction.find((c) => c.namespace === id)?.status === "running";

  return (
    <Box className={css.pad}>
      <Head label={ns.kind} />
      <Title>{ns.id}</Title>
      <Text fz="12.5px" mb={20}>
        <StatusDot status={ns.status} />
        <span style={{ color: "var(--vf-dim)" }}> · updated {ns.updated}</span>
      </Text>

      <Tiles
        items={[
          { k: "vectors", v: compact(ns.vectors) },
          { k: "size", v: bytes(ns.sizeBytes) },
          { k: "searches·24h", v: compact(ns.searches) },
          { k: "cache hit", v: percent(ns.cacheHit) },
        ]}
      />

      <Box mb={20}>
        <KvBox
          rows={[
            { k: "dimensions", v: ns.dims },
            { k: "distance_metric", v: ns.metric },
            { k: "embedder_profile", v: <span style={{ color: "var(--vf-acc)" }}>{ns.profile}</span> },
            { k: "bm25_enabled", v: String(ns.bm25) },
            { k: "base_version", v: ns.baseVer },
            { k: "delta_segments", v: ns.deltas },
            { k: "tombstones", v: ns.tombstones },
          ]}
        />
      </Box>

      <Group gap={10} wrap="wrap">
        <Button
          style={{ flex: 1 }}
          fz="13px"
          fw={600}
          onClick={() => {
            setQueryNamespace(ns.id);
            close();
            go("/query");
          }}
        >
          ▸ query
        </Button>
        <Button
          variant="default"
          fz="13px"
          onClick={() => {
            close();
            go("/documents");
          }}
        >
          docs
        </Button>
        {compacting ? (
          <Button variant="default" fz="13px" c="var(--vf-warn)" disabled>
            compacting…
          </Button>
        ) : (
          <Button
            variant="default"
            fz="13px"
            onClick={() => {
              void compactNamespace(ns.id).then(() =>
                notifications.show({ message: `compaction queued for ${ns.label}` }),
              );
            }}
          >
            compact
          </Button>
        )}
      </Group>

      <Group gap={10} mt={10} wrap="nowrap">
        <Button variant="default" style={{ flex: 1 }} fz="12.5px" onClick={() => openModal("renameNamespace")}>
          rename
        </Button>
        <Button
          style={{ flex: 1 }}
          fz="12.5px"
          color="red"
          variant="light"
          onClick={() => openModal("deleteNamespace")}
        >
          delete namespace
        </Button>
      </Group>
    </Box>
  );
}

// -------------------------------------------------------------- document ----

function DocumentDetail({ id }: { id: string }) {
  const close = useUiStore((s) => s.closeDetail);
  const doc = useConsoleStore((s) => s.documents.find((d) => d.id === id));
  const namespace = useConsoleStore((s) => s.documentsNamespace);
  const deleteDocument = useConsoleStore((s) => s.deleteDocument);

  if (!doc) return null;

  return (
    <Box className={css.pad}>
      <Head label="document" />
      <Title>{doc.id}</Title>

      <div className={css.sectionLabel}>// content</div>
      <Box className={css.contentBox} mb={18}>
        {doc.content}
      </Box>

      <div className={css.sectionLabel}>// metadata</div>
      <Box mb={18}>
        <KvBox rows={Object.entries(doc.metadata).map(([k, v]) => ({ k, v: String(v) }))} />
      </Box>

      {doc.vector?.length ? (
        <>
          <div className={css.sectionLabel}>
            // vector <span style={{ color: "var(--vf-dim2)" }}>(first dims)</span>
          </div>
          <Box className={css.vectorBox} mb={18}>
            [{doc.vector.map((v) => v.toFixed(3)).join(", ")}, …]
          </Box>
        </>
      ) : null}

      <Button
        fullWidth
        color="red"
        variant="light"
        fz="13px"
        onClick={() => {
          if (!namespace) return;
          modals.openConfirmModal({
            title: "delete document",
            labels: { confirm: "delete", cancel: "cancel" },
            confirmProps: { color: "red" },
            children: (
              <Text fz="12.5px" c="var(--vf-sec)" lh={1.7}>
                Tombstone {doc.id} in {namespace}. It stops matching queries immediately and is
                dropped from the base index at the next compaction.
              </Text>
            ),
            onConfirm: () => {
              void deleteDocument(namespace, doc.id).then(() => {
                notifications.show({ message: `deleted ${doc.id}`, color: "red" });
                close();
              });
            },
          });
        }}
      >
        delete document
      </Button>
    </Box>
  );
}

// ------------------------------------------------------------- operation ----

function OperationDetail({ id }: { id: string }) {
  const op = useConsoleStore((s) => s.operations.find((o) => o.id === id));
  if (!op) return null;

  return (
    <Box className={css.pad}>
      <Head label="operation" />
      <Title>{op.id}</Title>
      <Text fz="12.5px" mb={20}>
        <StatusDot status={op.status} />
        <span style={{ color: "var(--vf-dim)" }}> · {op.age} ago</span>
      </Text>

      <Box mb={20}>
        <KvBox
          rows={[
            { k: "namespace", v: op.namespace },
            { k: "kind", v: op.kind },
            { k: "documents", v: op.docs },
            { k: "log_pieces", v: op.pieces },
            { k: "delivery_attempt", v: `${op.attempt} / 5` },
            { k: "delta_sequence", v: op.seq },
          ]}
        />
      </Box>

      <div className={css.sectionLabel}>// lifecycle</div>
      <Box fz="12.5px" mb={20} style={{ lineHeight: 2.2 }}>
        <div style={{ color: "var(--vf-ok)" }}>✓ appended to ingest log (202 durability point)</div>
        <div style={{ color: "var(--vf-ok)" }}>✓ ringed waker · owner drained group</div>
        {op.status === "committed" ? (
          <div style={{ color: "var(--vf-ok)" }}>
            ✓ coalesced → delta segment · manifest CAS committed
          </div>
        ) : null}
        {op.status === "running" ? (
          <div style={{ color: "var(--vf-warn)" }}>
            ▸ coalescing into delta segment
            <Cursor />
          </div>
        ) : null}
        {op.status === "queued" ? (
          <div style={{ color: "var(--vf-dim)" }}>· awaiting owner drain</div>
        ) : null}
        {op.status === "failed" ? (
          <div style={{ color: "var(--vf-bad)" }}>✗ validation failed — moved to dead-letter</div>
        ) : null}
      </Box>

      {op.error ? <Box className={css.errorBox}>{op.error}</Box> : null}
    </Box>
  );
}

// ------------------------------------------------------------------- job ----

function jobSteps(kind: string, status: string) {
  const done = "var(--vf-ok)";
  const run = "var(--vf-warn)";
  const pend = "var(--vf-dim)";
  const fail = "var(--vf-bad)";
  if (kind === "compact") {
    return [
      { t: "acquire build gate", c: done },
      { t: "load base + deltas", c: done },
      { t: "merge segments", c: status === "done" ? done : run },
      { t: "commit manifest CAS", c: status === "done" ? done : pend },
      { t: "invalidate cache", c: status === "done" ? done : pend },
    ];
  }
  if (status === "failed") {
    return [
      { t: "mark indexing", c: done },
      { t: "clone repo (oauth)", c: fail },
      { t: "chunk (tree-sitter)", c: pend },
      { t: "embed documents", c: pend },
      { t: "build HNSW + BM25", c: pend },
      { t: "commit", c: pend },
    ];
  }
  return [
    { t: "mark indexing", c: done },
    { t: "clone repo (oauth)", c: done },
    { t: "chunk (tree-sitter)", c: status === "done" ? done : run },
    { t: "embed documents", c: status === "done" ? done : pend },
    { t: "build HNSW + BM25", c: status === "done" ? done : pend },
    { t: "commit + clear gc-pending", c: status === "done" ? done : pend },
  ];
}

function JobDetail({ id }: { id: string }) {
  const close = useUiStore((s) => s.closeDetail);
  const job = useConsoleStore((s) => s.jobs.find((j) => j.id === id));
  const cancelJob = useConsoleStore((s) => s.cancelJob);
  if (!job) return null;

  return (
    <Box className={css.pad}>
      <Head label={`${job.kind} job`} />
      <Title>{job.id}</Title>
      <Text fz="12.5px" mb={20}>
        <StatusDot status={job.status} />
        <span style={{ color: "var(--vf-dim)" }}> · {job.namespace}</span>
      </Text>

      <Box mb={20}>
        <KvBox
          rows={[
            { k: "started", v: job.started },
            { k: "duration", v: job.duration },
            { k: "note", v: job.note },
          ]}
        />
      </Box>

      <div className={css.sectionLabel}>// pipeline steps</div>
      <Box fz="12.5px" mb={20} style={{ lineHeight: 2.2 }}>
        {jobSteps(job.kind, job.status).map((s) => (
          <div key={s.t} style={{ color: s.c }}>
            ● {s.t}
          </div>
        ))}
      </Box>

      {job.error ? (
        <Box className={css.errorBox} mb={16}>
          {job.error}
        </Box>
      ) : null}

      {job.status === "queued" ? (
        <Button
          fullWidth
          color="red"
          variant="light"
          fz="13px"
          onClick={() => {
            void cancelJob(job.id).then(() => {
              notifications.show({ message: `canceled ${job.id}`, color: "red" });
              close();
            });
          }}
        >
          cancel queued job
        </Button>
      ) : null}
    </Box>
  );
}

// ------------------------------------------------------------------ repo ----

function RepoDetail({ id }: { id: string }) {
  const openRename = useUiStore((s) => s.openRename);
  const repo = useConsoleStore((s) => s.repos.find((r) => r.id === id));
  const reindexRepo = useConsoleStore((s) => s.reindexRepo);
  const repairWebhook = useConsoleStore((s) => s.repairWebhook);
  const compactNamespace = useConsoleStore((s) => s.compactNamespace);
  if (!repo) return null;

  return (
    <Box className={css.pad}>
      <Head label="code repository" />
      <Title>{repo.name}</Title>
      <Text fz="12.5px" mb={20}>
        <StatusDot status={repo.status} />
        <span style={{ color: "var(--vf-dim)" }}>
          {" "}
          · {compact(repo.vectors)} vectors · indexed {repo.lastIndexed}
        </span>
      </Text>

      <div className={css.sectionLabel}>// indexed branches</div>
      <Stack gap={6} mb={20}>
        {repo.branches.map((b) => (
          <Box key={b} className={css.branchRow}>
            <span style={{ color: "var(--vf-text)" }}>{b}</span>
            <span style={{ color: "var(--vf-ok)", fontSize: 11 }}>indexed</span>
          </Box>
        ))}
      </Stack>

      <div className={css.sectionLabel}>// webhook health</div>
      <Box mb={20}>
        <KvBox
          rows={[
            {
              k: "status",
              v: (
                <span style={{ color: repo.webhook === "healthy" ? "var(--vf-ok)" : "var(--vf-bad)" }}>
                  {repo.webhook}
                </span>
              ),
            },
            { k: "events", v: "push" },
            { k: "target", v: <span style={{ color: "var(--vf-sec)" }}>/v1/code/webhooks/github</span> },
          ]}
        />
      </Box>

      <Group gap={10} wrap="wrap">
        <Button
          // Basis, not plain flex:1 — with four actions in the row a bare
          // flex:1 squeezes the label to "▸ rei" instead of wrapping.
          style={{ flex: "1 1 118px" }}
          fz="13px"
          fw={600}
          onClick={() => {
            void reindexRepo(repo.namespace).then(() =>
              notifications.show({ message: `reindex queued for ${repo.name}` }),
            );
          }}
        >
          ▸ reindex
        </Button>
        <Button
          variant="default"
          fz="13px"
          onClick={() => {
            void compactNamespace(repo.namespace).then(() =>
              notifications.show({ message: `compaction queued for ${repo.name}` }),
            );
          }}
        >
          compact
        </Button>
        <Button
          variant="default"
          fz="13px"
          onClick={() => openRename({ kind: "repo", id: repo.id, current: repo.name })}
        >
          rename
        </Button>
        {repo.webhook !== "healthy" ? (
          <Button
            variant="default"
            fz="13px"
            onClick={() => {
              void repairWebhook(repo.namespace).then(() =>
                notifications.show({ message: `webhook repaired for ${repo.name}`, color: "green" }),
              );
            }}
          >
            repair webhook
          </Button>
        ) : null}
      </Group>
    </Box>
  );
}

// ---------------------------------------------------------------- member ----

function MemberDetail({ id }: { id: string }) {
  const member = useWorkspaceStore((s) => s.members.find((m) => m.uid === id));
  const workspaceId = useWorkspaceStore((s) => s.currentId);
  if (!member) return null;

  const notImplemented = (what: string) =>
    notifications.show({ message: `${what} has no endpoint yet — see ROADMAP.md`, color: "yellow" });

  return (
    <Box className={css.pad}>
      <Head label="member" />
      <Group gap={12} mb={22} wrap="nowrap">
        <span className={css.avatarLg}>{member.initials}</span>
        <Box>
          <Text fz="15px" c="var(--vf-text)">
            {member.name}
          </Text>
          <Text fz="12.5px" c="var(--vf-sec)">
            {member.email}
          </Text>
        </Box>
      </Group>

      <Box mb={20}>
        <KvBox
          rows={[
            { k: "firebase_uid", v: member.uid },
            { k: "role", v: <span style={{ color: "var(--vf-acc)" }}>{member.role}</span> },
            { k: "grants", v: workspaceId ? `${workspaceId}/*` : "—" },
            { k: "last_active", v: member.lastActive },
          ]}
        />
      </Box>

      <Group gap={10} wrap="nowrap">
        <Button variant="default" style={{ flex: 1 }} fz="13px" onClick={() => notImplemented("role change")}>
          change role
        </Button>
        <Button color="red" variant="light" fz="13px" onClick={() => notImplemented("member removal")}>
          remove
        </Button>
      </Group>
    </Box>
  );
}

// ------------------------------------------------------------------- key ----

function KeyDetail({ id }: { id: string }) {
  const close = useUiStore((s) => s.closeDetail);
  const openRename = useUiStore((s) => s.openRename);
  const key = useConsoleStore((s) => s.keys.find((k) => k.id === id));
  const rotateKey = useConsoleStore((s) => s.rotateKey);
  const revokeKey = useConsoleStore((s) => s.revokeKey);
  if (!key) return null;

  return (
    <Box className={css.pad}>
      <Head label="api key" />
      <Title>{key.name}</Title>
      <Text fz="13px" c="var(--vf-sec)" mb={20}>
        {key.masked}
      </Text>

      <Box mb={16}>
        <KvBox
          rows={[
            { k: "key_id", v: key.id },
            { k: "grants", v: key.grants },
            { k: "calls · 30d", v: key.calls },
            { k: "created", v: key.created },
          ]}
        />
      </Box>

      <div className={css.sectionLabel}>// capability scopes</div>
      <Group gap={6} mb={20} wrap="wrap">
        {key.scopes.map((s) => (
          <span key={s} className={css.scopeChipLg}>
            {s}
          </span>
        ))}
      </Group>

      <Group gap={10} wrap="nowrap">
        <Button
          variant="light"
          style={{ flex: 1 }}
          fz="13px"
          styles={{ root: { background: "var(--vf-accbg)", border: "1px solid var(--vf-border)", color: "var(--vf-acc)" } }}
          onClick={() => {
            void rotateKey(key.id).then(() => {
              notifications.show({ message: `rotated ${key.name} — copy the new secret` });
              close();
            });
          }}
        >
          rotate
        </Button>
        <Button
          color="red"
          variant="light"
          style={{ flex: 1 }}
          fz="13px"
          onClick={() =>
            modals.openConfirmModal({
              title: "revoke api key",
              labels: { confirm: "revoke key", cancel: "cancel" },
              confirmProps: { color: "red" },
              children: (
                <Box fz="12.5px" c="var(--vf-sec)" lh={1.7}>
                  <Box c="var(--vf-bad)" mb={10}>$ vf keys revoke</Box>
                  Revoke {key.name} immediately. Any service still using it starts receiving 401 on
                  the next request.
                </Box>
              ),
              onConfirm: () => {
                void revokeKey(key.id).then(() => {
                  notifications.show({ message: `revoked ${key.name}`, color: "red" });
                  close();
                });
              },
            })
          }
        >
          revoke
        </Button>
      </Group>

      <Group gap={10} mt={10}>
        <Button
          variant="default"
          style={{ flex: 1 }}
          fz="12.5px"
          onClick={() => openRename({ kind: "key", id: key.id, current: key.name })}
        >
          rename
        </Button>
      </Group>
    </Box>
  );
}

// --------------------------------------------------------------- profile ----

function ProfileDetail({ id }: { id: string }) {
  const profile = useConsoleStore((s) => s.profiles.find((p) => p.name === id));
  const namespaces = useConsoleStore((s) => s.namespaces);
  if (!profile) return null;

  const usedBy = namespaces.filter((n) => n.profile === profile.name);

  return (
    <Box className={css.pad}>
      <Head label="embedder profile" />
      <Title>{profile.name}</Title>
      <Text fz="13px" c="var(--vf-sec)" mb={20}>
        {profile.provider} · {profile.dims}d
      </Text>

      <Box mb={16}>
        <KvBox
          rows={[
            { k: "dimensions", v: profile.dims },
            { k: "query_prefix", v: profile.queryPrefix },
            { k: "document_prefix", v: profile.documentPrefix },
          ]}
        />
      </Box>

      <div className={css.sectionLabel}>// namespaces using this profile</div>
      {usedBy.length > 0 ? (
        <Stack gap={6} mb={20}>
          {usedBy.map((n) => (
            <Box key={n.id} className={css.usedByRow}>
              {n.label}
            </Box>
          ))}
        </Stack>
      ) : (
        <Box className={css.noUsers} mb={20}>
          no namespaces use this profile yet
        </Box>
      )}

      <Text fz="11.5px" c="var(--vf-dim2)" lh={1.6}>
        // catalog recipes are immutable — a new model or dims is a new profile plus a rebuild.
      </Text>
    </Box>
  );
}
